import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../admin-page.css";
import "./lightning-admin.css";
import { lightningSubscriberAPI, userAPI } from "../../../api";
import { LassieApi } from "../../../api_client/Lassie";
import AdminHeader from "../admin-header/AdminHeader";
import WplButton from "../../../components/wpl-button/WplButton";
import WplPopup from "../../../components/wpl-popup-window/WplPopup";
import WindfarmToggleList from "../WindfarmToggleList";
import { SortableTh, sortRows, useClientSort } from "../SortableTable";

const byCustomerThenName = (a, b) =>
  a.customer !== b.customer ? (a.customer || "").localeCompare(b.customer || "") : a.name.localeCompare(b.name);

function ScopeBadge({ scope }) {
  return <span className={`scope-badge scope-${scope}`}>{scope || "—"}</span>;
}

export default function LightningAdmin() {
  const [users, setUsers] = useState([]);
  const [windfarms, setWindfarms] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, onSort] = useClientSort({ key: "email", dir: "asc" });

  const reloadSubscribers = useCallback(() => lightningSubscriberAPI.list_all_lightning_subscribers().then(setSubscribers), []);

  useEffect(() => {
    Promise.all([
      userAPI.listUsers(false).then(setUsers),
      LassieApi.list_windfarms.f().then(setWindfarms),
      reloadSubscribers(),
    ]).finally(() => setLoading(false));
  }, [reloadSubscribers]);

  const sortedWindfarms = useMemo(() => [...windfarms].sort(byCustomerThenName), [windfarms]);

  // Subscribed wind farm names per user, for the count column and its tooltip.
  const subscriptionsByUser = useMemo(() => {
    const map = new Map();
    subscribers.forEach((s) => {
      if (!map.has(s.user_id)) map.set(s.user_id, []);
      map.get(s.user_id).push(s.windfarm_name);
    });
    return map;
  }, [subscribers]);

  const rows = useMemo(
    () =>
      users
        .filter((u) => !u.is_deleted)
        .map((u) => {
          const names = subscriptionsByUser.get(u.id) || [];
          return { ...u, subscriptionCount: names.length, subscriptionNames: names.sort().join(", ") };
        }),
    [users, subscriptionsByUser],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.firstname || "").toLowerCase().includes(q) ||
        u.subscriptionNames.toLowerCase().includes(q),
    );
  }, [rows, search]);
  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);

  return (
    <div className="admin-page lightning-admin-page">
      <div className="admin-wrapper">
        <AdminHeader />

        <div className="admin-title-row">
          <h1>Lightning user subscription</h1>
        </div>
        <p className="section-hint">
          Subscribed users receive Lassie lightning notifications for the wind farms below. Pick a user to manage their
          subscriptions.
        </p>

        <div className="admin-content">
          {loading && <p className="empty-state">Loading…</p>}
          {!loading && (
            <div>
              <div className="section-toolbar">
                <input
                  className="search-input"
                  placeholder="Search by email, name or wind farm…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="count-label">
                  {filtered.length} / {rows.length} users
                </span>
              </div>

              <div className="table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <SortableTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
                      <SortableTh label="Name" sortKey="firstname" sort={sort} onSort={onSort} />
                      <SortableTh label="Scope" sortKey="scope" sort={sort} onSort={onSort} />
                      <SortableTh label="Subscribed wind farms" sortKey="subscriptionCount" sort={sort} onSort={onSort} />
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((u) => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td>{u.firstname || "—"}</td>
                        <td>
                          <ScopeBadge scope={u.scope} />
                        </td>
                        <td title={u.subscriptionNames}>
                          {u.subscriptionCount > 0 ? (
                            <span className="subscription-count">
                              {u.subscriptionCount}
                              <span className="subscription-names">{u.subscriptionNames}</span>
                            </span>
                          ) : (
                            <span className="muted">None</span>
                          )}
                        </td>
                        <td className="actions-cell">
                          <WplButton className="small" value="Manage" onClick={() => setSelectedUser(u)} />
                        </td>
                      </tr>
                    ))}
                    {sorted.length === 0 && (
                      <tr>
                        <td className="table-message" colSpan={5}>
                          No users match your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <WplPopup className="admin-popup" showPopup={Boolean(selectedUser)} closePopup={() => setSelectedUser(null)}>
        {selectedUser && (
          <SubscriptionPanel
            user={selectedUser}
            windfarms={sortedWindfarms}
            onChanged={reloadSubscribers}
            onClose={() => setSelectedUser(null)}
          />
        )}
      </WplPopup>
    </div>
  );
}

function SubscriptionPanel({ user, windfarms, onChanged, onClose }) {
  const [subscriptions, setSubscriptions] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    lightningSubscriberAPI
      .list_user_subscriptions_by_user_id(user.id)
      .then(setSubscriptions)
      .catch(() => setSubscriptions([]));
  }, [user.id]);

  const subscribedIds = useMemo(() => new Set((subscriptions || []).map((s) => s.windfarm_id)), [subscriptions]);

  function toggle(wf) {
    setError("");
    const call = subscribedIds.has(wf.id)
      ? lightningSubscriberAPI.unsubscribe(user.id, wf.id)
      : lightningSubscriberAPI.subscribe(user.id, wf.id);
    return call
      .then((updated) => {
        setSubscriptions(updated);
        return onChanged();
      })
      .catch((err) => {
        const data = err?.response?.data;
        setError(typeof data === "string" && data.trim() ? data.trim() : "Could not update the subscription.");
      });
  }

  return (
    <div className="edit-user-panel">
      <h2>{user.firstname || user.email}</h2>
      <p className="user-email">
        {user.email} · <ScopeBadge scope={user.scope} />
      </p>

      <section>
        <h3>Lightning notification subscriptions</h3>
        <WindfarmToggleList
          windfarms={windfarms}
          assignedIds={subscribedIds}
          onToggle={toggle}
          loading={subscriptions === null}
          countLabel="subscribed"
        />
      </section>

      {error && <p className="error-message">{error}</p>}

      <div className="panel-actions">
        <WplButton className="subtle" value="Close" onClick={onClose} />
      </div>
    </div>
  );
}
